import React from 'react';
import {
  Text,
  RichText,
  Image,
  Link,
  withSitecoreContext,
  Placeholder,
} from '@sitecore-jss/sitecore-jss-react';
import './styles.css';
import { addStyle, getBackgroundImages } from '../SharedUtils/utils';
import { useTheme, useInvertedColor } from '../SharedUtils/hooks';
import { getBackgroundColor } from '../SharedUtils/themeColors';

const MastheadSliderElement = ({ fields, rendering, sitecoreContext }) => {
  const isEditorMode = sitecoreContext.pageEditing;
  const lang = sitecoreContext.language;

  const { isLight } = useTheme();

  const {
    largeBackgroundImageField: largeImage,
    mediumBackgroundImageField: mediumImage,
    smallBackgroundImageField: smallImage,
    largeBackgroundImageSrc: backgroundImageLargeSrc,
    mediumBackgroundImageSrc: backgroundImageMediumSrc,
    smallBackgroundImageSrc: backgroundImageSmallSrc,
  } = getBackgroundImages(fields, isLight);

  const widgetClasses = ['c-masthead__slider__item__content'];
  const widgetStyles = {};
  const uniqueId = rendering.uid;

  const isInvertedColor = useInvertedColor(fields);
  const invertedColourClass = isInvertedColor ? 'is-inverted' : '';
  const anchorImageClass = fields.UseAnchoredBackgroundImage?.value ? 'u-bg-h-align-right' : '';
  const centerAlignContentClass = fields.CenterAlignContent?.value ? 'u-align-center' : '';
  const rightAlignContentClass = fields.RightAlignContent?.value ? 'u-align-float-right' : '';
  const middleAlignContentClass = fields.VerticalCenterAlign?.value
    ? 'u-align-vertical-center'
    : '';

  let ctaPrimaryClasses = fields.CTAPrimaryButtonStyle?.value
    ? 'c-button c-button--cta-invert'
    : 'c-button c-button--cta';
  let ctaSecondaryClasses = fields.CTASecondaryButtonStyle?.value
    ? 'c-button c-button--cta-invert'
    : 'c-button c-button--cta';

  const showStaticContentAfterVideo = fields.ShowStaticContentAfterVideo?.value;
  const fullLengthVideo = fields.FullLengthVideo;

  const headerImageIsMixBlendModeClass = fields.mixBlendMode?.value ? 'is-mixblendmode' : '';

  const videoUrl = fields.Video?.value?.src || '';
  const introVideoVtt = fields.IntroVideoVTT?.value?.src || '';
  const fullVideoVtt = fields.FullVideoVTT?.value?.src || '';

  const swapTitle = fields.SwapTitleSubTitle?.value;
  const isDarkModePrimaryCta = fields.DarkModePrimaryButton?.value;
  const isDarkModeSecondaryCta = fields.DarkModeSecondaryButton?.value;
  const showDownArrow = fields.ShowDownArrow?.value;
  const showVideoAction = fields.ShowVideoAction?.value;
  const skipVideoText = fields.SkipVideoText?.value;
  const fullLengthVideoUrl = fullLengthVideo?.value?.src ? fullLengthVideo?.value?.src : '';
  const playFullLengthVideoText = fields.PlayFullLengthVideoText?.value;
  const invertedIndicatorColourClass = fields.InvertColorIndicator?.value
    ? 'is-inverted-indicator-flag'
    : '';

  if (isDarkModePrimaryCta) ctaPrimaryClasses = 'c-button c-button--cta--dm--primary';
  if (isDarkModeSecondaryCta) ctaSecondaryClasses = 'c-button c-button--cta--dm--secondary';

  const isBackgroundImageEmpty =
    !backgroundImageSmallSrc && !backgroundImageMediumSrc && !backgroundImageLargeSrc && videoUrl;

  const useVideo = isBackgroundImageEmpty || showStaticContentAfterVideo;
  const useBackgroundImage = !isBackgroundImageEmpty || showStaticContentAfterVideo;

  const videoAttribute = !showStaticContentAfterVideo ? { loop: true } : {};
  const videoClass = videoUrl ? 'is-play is-autoplay' : 'is-stop';

  const videoJs = `
    var lang="${lang}";
    var introVideoUrl = "${videoUrl}&lang=${lang}";
    var fullLengthVideoUrl = "${fullLengthVideoUrl}&lang${lang}";
  `;

  let alt = '';

  if (largeImage?.value?.alt) {
    alt = largeImage.value.alt;
  } else if (mediumImage?.value?.alt) {
    alt = mediumImage.value.alt;
  } else if (smallImage?.value?.alt) {
    alt = smallImage.value.alt;
  }

  let srcSet = '';
  if (backgroundImageSmallSrc) {
    srcSet += `${backgroundImageSmallSrc} ${smallImage.Width}w`;
  }

  if (backgroundImageMediumSrc) {
    srcSet += `${backgroundImageMediumSrc} ${mediumImage.Width}w`;
  }

  if (backgroundImageLargeSrc) {
    srcSet += `${backgroundImageLargeSrc} ${largeImage.Width}w`;
  }

  const titleColor = isLight
    ? fields.TitleColorLight?.fields?.HexColorCode?.value
    : fields.TitleColorDark?.fields?.HexColorCode?.value;
  titleColor && widgetClasses.push('override-title-color');
  addStyle(widgetStyles, '--c-masthead-slider-item-title-color', titleColor);
  const titleDropShadow = isLight
    ? fields.TitleDropShadowLight?.value
    : fields.TitleDropShadowDark?.value;
  addStyle(widgetStyles, '--c-masthead-slider-item-title-dropshadow', titleDropShadow);

  const subtitleColor = isLight
    ? fields.subtitleColorLight?.fields?.HexColorCode?.value
    : fields.subtitleColorDark?.fields?.HexColorCode?.value;
  subtitleColor && widgetClasses.push('override-subtitle-color');
  addStyle(widgetStyles, '--c-masthead-slider-item-subtitle-color', subtitleColor);
  const subTitleDropShadow = isLight
    ? fields.subtitleDropShadowLight?.value
    : fields.subtitleDropShadowDark?.value;
  addStyle(widgetStyles, '--c-masthead-slider-item-subtitle-dropshadow', subTitleDropShadow);

  addStyle(
    widgetStyles,
    '--c-masthead-slider-item-description-width-desktop',
    fields.DescriptionWidthDesktop?.value
  );
  addStyle(
    widgetStyles,
    '--c-masthead-slider-item-description-width-tablet',
    fields.DescriptionWidthTablet?.value
  );
  addStyle(
    widgetStyles,
    '--c-masthead-slider-item-description-width-mobile',
    fields.DescriptionWidthMobile?.value
  );
  addStyle(
    widgetStyles,
    '--c-masthead-slider-item-description-numb-ellipses',
    fields.NumLineShowEllipses?.value
  );

  const backgroundColor = getBackgroundColor(fields, isLight);
  addStyle(widgetStyles, 'backgroundColor', backgroundColor);

  widgetClasses.push(`u-ellipses${fields.DisableEllipses?.value ? '-disabled' : ''}`);

  const renderTitle = (lineClass) => {
    if (!isEditorMode && !fields.Title?.value) {
      return <></>;
    }

    const strippedValue = removeParagraphs(fields.Title?.value);

    return (
      <div className={lineClass}>
        <div
          tabIndex="0"
          className="c-masthead__slider__item__content__theme__title"
          aria-label={fields.TitleAreaLabel?.value}
        >
          {isEditorMode && <RichText field={fields.Title} />}
          {!isEditorMode && <div dangerouslySetInnerHTML={{ __html: strippedValue }}></div>}
          {renderHorizontalRule()}
        </div>
      </div>
    );
  };

  const renderSubTitle = (lineClass) => {
    if (!isEditorMode && !fields.Subtitle?.value) {
      return <></>;
    }

    const strippedValue = removeParagraphs(fields.Subtitle?.value);

    return (
      <div className={lineClass}>
        <div
          tabIndex="0"
          className={
            `c-masthead__slider__item__content__theme__subtitle` +
            (fields.Title?.value && ' u-display-inline')
          }
          aria-label={fields.SubTitleAreaLabel?.value}
        >
          {isEditorMode && <RichText field={fields.Subtitle} />}
          {!isEditorMode && strippedValue}
          {renderHorizontalRule()}
        </div>
      </div>
    );
  };

  const removeParagraphs = (text) => {
    return text?.replace('<p>', '').replace('</p>', '');
  };

  const renderDescription = () => {
    if (!isEditorMode && !fields.Title?.value) {
      return <></>;
    }

    return (
      <div className="c-masthead__slider__item__content__theme__line3">
        <div
          tabIndex="0"
          className="c-masthead__slider__item__content__theme__description"
          aria-label={fields.DescriptionAreaLabel?.value}
        >
          <RichText field={fields.Description} />
        </div>
      </div>
    );
  };

  const renderHorizontalRule = () => {
    const horizontalRuleCss = fields.UseHorizontalRule?.value
      ? 'c-masthead__slider__hr--visible'
      : 'c-masthead__slider__hr';
    const horizontalRuleColor = fields.HorizontalRuleColor?.fields?.HexColorCode?.value;

    return <></>;
  };

  return (
    <div
      id={`c-${uniqueId}`}
      className={`c-masthead__slider__item ${invertedColourClass} ${invertedIndicatorColourClass} ${anchorImageClass}`}
    >
      <div
        className={widgetClasses.join(' ')}
        style={widgetStyles}
        data-content-after-video={showStaticContentAfterVideo}
        data-background-image-large={backgroundImageLargeSrc}
        data-background-image-medium={backgroundImageMediumSrc}
        data-background-image-small={backgroundImageSmallSrc}
      >
        {useVideo && (
          <>
            <script>{videoJs}</script>
            <video id="isAlwaysPlay" className={videoClass} muted playsInline {...videoAttribute}>
              <source src={videoUrl} type="video/mp4" />
              {introVideoVtt && (
                <track label={videoUrl} kind="subtitles" srcLang={lang} src={introVideoVtt} />
              )}
              {fullVideoVtt && (
                <track
                  label={fullLengthVideoUrl}
                  kind="subtitles"
                  srcLang={lang}
                  src={fullVideoVtt}
                />
              )}
              Your browser does not support HTML5 video.
            </video>
          </>
        )}

        {useBackgroundImage && (
          <picture className="c-masthead__slider__item__content__background">
            {backgroundImageSmallSrc && (
              <source
                srcSet={backgroundImageSmallSrc}
                media={`(max-width: ${smallImage?.value.width}px)`}
              />
            )}
            {backgroundImageMediumSrc && (
              <source
                srcSet={backgroundImageMediumSrc}
                media={`(max-width: ${mediumImage?.value.width}px)`}
              />
            )}
            {backgroundImageLargeSrc && (
              <source
                srcSet={backgroundImageLargeSrc}
                media={`(max-width: ${largeImage?.value.width}px)`}
              />
            )}
            <img src={backgroundImageLargeSrc} alt={alt} />
          </picture>
        )}

        <div className="c-masthead__slider__item__content__inspiration">
          <div className="c-container full-height">
            <div
              className={`c-masthead__slider__item__content__theme ${rightAlignContentClass} ${centerAlignContentClass} ${middleAlignContentClass}`}
            >
              <Placeholder name="jss-masterhead-elements-breadcrumbs" rendering={rendering} />
              <div className="c-masthead__indicator__title__placeholder is-hidden">
                {fields.SliderTitle?.value}
              </div>

              {(isEditorMode || fields.HeaderImage?.value?.src) && (
                <div className="c-masthead__slider__item__content__theme__image">
                  <Image className={headerImageIsMixBlendModeClass} media={fields.HeaderImage} />
                </div>
              )}

              {swapTitle ? (
                <>
                  {renderTitle('c-masthead__slider__item__content__theme__line1')}
                  {renderSubTitle('c-masthead__slider__item__content__theme__line2')}
                </>
              ) : (
                <>
                  {renderSubTitle('c-masthead__slider__item__content__theme__line1')}
                  {renderTitle('c-masthead__slider__item__content__theme__line2')}
                </>
              )}

              {renderDescription()}

              <div className="c-masthead__slider__item__content__theme__cta">
                {(isEditorMode || fields.CallToActionLink?.value?.href) && (
                  <Link
                    field={fields.CallToActionLink}
                    className={ctaPrimaryClasses}
                    role="button"
                  />
                )}

                {(isEditorMode || fields.CallToActionSecondaryButton?.value?.href) && (
                  <Link
                    field={fields.CallToActionSecondaryButton}
                    className={ctaSecondaryClasses}
                    role="button"
                  />
                )}

                {(isEditorMode || playFullLengthVideoText) && (
                  <div
                    tabIndex="0"
                    aria-label={playFullLengthVideoText}
                    title={fields.PlayFullLengthVideoAltText?.value}
                    className={
                      'c-masthead__slider__item__content__theme__cta__video ' + ctaSecondaryClasses
                    }
                    data-videoUrl={fullLengthVideoUrl}
                  >
                    {playFullLengthVideoText}
                  </div>
                )}

                {fields.UsePlayPauseButton?.value && (
                  <div
                    className={`c-button--video ${
                      fields.InvertColorPlayPauseButton?.value && 'is-inverted'
                    }`}
                    role="button"
                    sr-label-play={fields.PlayText?.value}
                    sr-label-pause={fields.PauseText?.value}
                    sr-icon-play="far fa-play-circle"
                    sr-icon-pause="far fa-pause-circle"
                  ></div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="c-masthead__slider__item__content__footer">
          {showVideoAction && (
            <>
              {(isEditorMode || skipVideoText) && (
                <div className="c-masthead__slider__item__content__footer__skip is-skip tooltip">
                  <span className="tooltiptext">
                    <Text field={fields.SkipVideoText} />
                  </span>
                  <i
                    tabIndex="0"
                    role="button"
                    aria-label={fields.SkipVideoText?.value}
                    className="far fa-stop-circle"
                  ></i>
                </div>
              )}

              {(isEditorMode || fields.StopVideoText?.value) && (
                <div className="c-masthead__slider__item__content__footer__stop is-stop tooltip">
                  <span className="tooltiptext">
                    <Text field={fields.StopVideoText} />
                  </span>
                  <i
                    tabIndex="0"
                    role="button"
                    aria-label={fields.StopVideoText?.value}
                    className="far fa-stop-circle"
                  ></i>
                </div>
              )}

              {(isEditorMode || fields.MuteVideoText?.value) && (
                <div className="c-masthead__slider__item__content__footer__mute is-mute tooltip">
                  <span className="tooltiptext">
                    <Text field={fields.MuteVideoText} />
                  </span>
                  <i
                    tabIndex="0"
                    role="button"
                    aria-label={fields.MuteVideoText?.value}
                    className="far fa-volume"
                  ></i>
                </div>
              )}

              {(isEditorMode || fields.UnmuteVideoText?.value) && (
                <div className="c-masthead__slider__item__content__footer__mute is-unmute tooltip">
                  <span className="tooltiptext">
                    <Text field={fields.UnmuteVideoText} />
                  </span>
                  <i
                    tabIndex="0"
                    role="button"
                    aria-label={fields.UnmuteVideoText?.value}
                    className="far fa-volume-slash"
                  ></i>
                </div>
              )}
            </>
          )}

          {showDownArrow && (
            <div className="c-masthead__slider__item__content__footer__moreDown">
              <i
                tabIndex="0"
                aria-label="Scroll down"
                role="button"
                className="fal fa-chevron-down"
              ></i>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default withSitecoreContext()(MastheadSliderElement);
